import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { reviews, faqs, statsData } from '@/mocks/manga';

export default function AboutPage() {
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://waiwaimanhua.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://waiwaimanhua.com/about' },
    ],
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '歪歪漫画网',
    url: 'https://waiwaimanhua.com/',
    logo: 'https://waiwaimanhua.com/logo.png',
    description: '歪歪漫画网是最新高清免费韩国漫画大全平台，主打热门无删减韩漫在线阅读，汇集12,800+部精品韩国漫画作品。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@waiwaimanhua.com',
      availableLanguage: ['zh-CN'],
    },
    sameAs: [
      'https://weibo.com/waiwaimanhua',
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '9.3',
      bestRating: '10',
      reviewCount: '5600000',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <Navbar />
      <main className="pt-20 bg-background-50 min-h-screen">
        {/* Hero */}
        <section className="relative w-full h-[280px] md:h-[380px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20artistic%20illustration%20of%20cozy%20reading%20space%2C%20warm%20ambient%20lighting%2C%20comic%20books%20stacked%20artistically%2C%20soft%20cream%20and%20beige%20tones%2C%20painterly%20texture%2C%20inviting%20atmosphere%2C%20editorial%20art%20style&width=1600&height=700&seq=about-hero-v2&orientation=landscape"
            alt="歪歪漫画网品牌背景"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-3">
                关于歪歪漫画网
              </h1>
              <p className="text-white/80 text-sm md:text-base max-w-lg mx-auto">
                致力于为韩漫爱好者提供最优质的免费阅读体验
              </p>
            </div>
          </div>
        </section>

        {/* Brand Intro */}
        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <p className="text-xs text-foreground-500 uppercase tracking-widest mb-2">OUR STORY</p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
              品牌故事
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              歪歪漫画网成立于2024年，是中国领先的韩国漫画在线阅读平台。我们始终坚持「免费、高清、完整」的理念，
              与韩国多家知名漫画出版社合作，第一时间将最新韩漫作品带给广大读者。从最初的几百部作品，
              发展到如今覆盖恋爱、奇幻、热血、悬疑、搞笑等全品类的漫画帝国，歪歪漫画网始终以读者体验为核心，
              打造了零广告干扰、极速加载、完美适配手机的阅读环境，累计服务超过560万韩漫爱好者。
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {statsData.map((stat) => (
              <div key={stat.label} className="text-center bg-background-100 rounded-xl p-6">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className={`${stat.icon} text-accent-600 text-xl`}></i>
                </div>
                <p className="font-heading text-2xl md:text-3xl font-black text-foreground-900 mb-1">
                  {stat.value}
                </p>
                <p className="text-sm text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="mb-8 text-center">
            <p className="text-xs text-foreground-500 uppercase tracking-widest mb-2">WHY US</p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
              为什么选择歪歪漫画网
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                icon: 'ri-vip-crown-line',
                title: '完全免费',
                desc: '所有韩国漫画完全免费阅读，无需VIP会员或付费解锁章节，让每一位读者都能畅享韩漫魅力。',
              },
              {
                icon: 'ri-hd-line',
                title: '高清画质',
                desc: '采用高清原画扫描技术，确保每一页漫画都呈现最完美的画质，细节清晰可见。',
              },
              {
                icon: 'ri-shield-check-line',
                title: '内容完整',
                desc: '承诺提供无删减完整版韩国漫画，忠实还原原作内容，呈现最原汁原味的阅读体验。',
              },
              {
                icon: 'ri-flashlight-line',
                title: '极速更新',
                desc: '热门连载漫画基本与韩国同步更新，通常在原作发布24小时内完成翻译上架。',
              },
              {
                icon: 'ri-smartphone-line',
                title: '多端适配',
                desc: '完美适配手机、平板和电脑，无论您使用什么设备，都能获得流畅的阅读体验。',
              },
              {
                icon: 'ri-team-line',
                title: '专业翻译',
                desc: '拥有经验丰富的翻译团队，确保每一句台词都精准到位，让读者感受原著精髓。',
              },
            ].map((item) => (
              <div key={item.title} className="bg-background-50 rounded-xl p-5 md:p-6">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-3">
                  <i className={`${item.icon} text-primary-600 text-lg`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* User Reviews */}
        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="mb-8 text-center">
            <p className="text-xs text-foreground-500 uppercase tracking-widest mb-2">TESTIMONIALS</p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
              读者口碑
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {reviews.map((review) => (
              <div key={review.id} className="bg-background-100 rounded-xl p-5 md:p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i
                      key={i}
                      className={`text-sm ${
                        i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'
                      }`}
                    ></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  &ldquo;{review.content}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <img
                    src={review.avatar}
                    alt={review.userName}
                    className="w-10 h-10 rounded-full object-cover"
                    loading="lazy"
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{review.userName}</p>
                    <p className="text-xs text-foreground-400">{review.date} · {review.mangaTitle}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-xs text-foreground-500 uppercase tracking-widest mb-2">CONTACT</p>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
                联系我们
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                {
                  icon: 'ri-mail-line',
                  title: '邮箱联系',
                  desc: 'contact@waiwaimanhua.com',
                  action: 'mailto:contact@waiwaimanhua.com',
                  actionText: '发送邮件',
                },
                {
                  icon: 'ri-wechat-line',
                  title: '微信公众号',
                  desc: '歪歪漫画网官方',
                  action: '#',
                  actionText: '关注公众号',
                },
                {
                  icon: 'ri-qq-line',
                  title: '读者交流群',
                  desc: 'QQ群：123456789',
                  action: '#',
                  actionText: '加入群聊',
                },
              ].map((item) => (
                <div key={item.title} className="bg-background-50 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                    <i className={`${item.icon} text-secondary-600 text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-foreground-500 mb-3">{item.desc}</p>
                  <a
                    href={item.action}
                    className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                    {...(item.action.startsWith('mailto:') ? {} : { rel: 'nofollow' })}
                  >
                    {item.actionText} <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="mb-8 text-center">
            <p className="text-xs text-foreground-500 uppercase tracking-widest mb-2">FAQ</p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
              常见问题解答
            </h2>
          </div>
          <div className="max-w-2xl mx-auto space-y-3">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="bg-background-100 rounded-xl overflow-hidden group"
              >
                <summary className="flex items-center justify-between p-4 md:p-5 text-left cursor-pointer list-none">
                  <h4 className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                    <a href={`#faq-${index}`}>{faq.question}</a>
                  </h4>
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-foreground-100 flex items-center justify-center group-open:bg-accent-500 group-open:text-background-50 transition-all duration-300">
                    <i className="ri-add-line text-sm group-open:rotate-45 transition-transform duration-300"></i>
                  </div>
                </summary>
                <div className="px-4 md:px-5 pb-4 md:pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </details>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}